"use client";

import { useEffect, useRef, useState } from "react";

import type { TocItem } from "@/lib/extract-toc-items";

interface TableOfContentsProps {
  items: TocItem[];
  variant: "desktop" | "mobile";
}

export function TableOfContents({ items, variant }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (items.length === 0) return;

    const headingIds = items.map((item) => item.id);

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          // 最上部に近いものをアクティブにする
          const topEntry = visibleEntries.reduce((prev, current) => {
            return prev.boundingClientRect.top < current.boundingClientRect.top ? prev : current;
          });
          setActiveId(topEntry.target.id);
        }
      },
      {
        rootMargin: "-88px 0px -60% 0px",
        threshold: 0,
      },
    );

    headingIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [items]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setIsOpen(false);
  };

  if (items.length === 0) {
    return null;
  }

  const TocList = () => (
    <ul className="space-y-0.5">
      {items.map((item) => {
        const isActive = activeId === item.id;
        const paddingLeft = item.level === 1 ? "12px" : item.level === 2 ? "12px" : "24px";
        return (
          <li key={item.id}>
            <button
              onClick={() => handleClick(item.id)}
              className="w-full cursor-pointer border-l-2 py-1.5 text-left text-[13px] transition-colors duration-200"
              style={{
                paddingLeft,
                paddingRight: "8px",
                borderLeftColor: isActive ? "var(--theme-primary)" : "transparent",
                color: isActive ? "var(--theme-primary)" : "#6B7280",
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {item.text}
            </button>
          </li>
        );
      })}
    </ul>
  );

  if (variant === "desktop") {
    return (
      <nav
        aria-label="目次"
        className="hidden w-[240px] shrink-0 lg:block"
        style={{
          position: "sticky",
          top: "88px",
          maxHeight: "calc(100vh - 112px)",
          overflowY: "auto",
        }}
      >
        <p className="mb-4 text-[13px] font-bold tracking-wide" style={{ color: "#9CA3AF" }}>
          目次
        </p>
        <TocList />
      </nav>
    );
  }

  return (
    <div className="mb-8 rounded-lg lg:hidden" style={{ backgroundColor: "#F9FAFB" }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-2 text-[14px] font-semibold text-[#374151]">
          <span>📋</span>
          <span>目次</span>
        </span>
        <span
          className="text-[#6B7280] transition-transform duration-200"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="border-t border-[#E5E7EB] px-4 pb-3 pt-2">
          <TocList />
        </div>
      )}
    </div>
  );
}
